import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { SEOHead, buildPersonSchema } from '../components/SEOHead';
import { Mail, GraduationCap, BookOpen } from 'lucide-react';
import axios from 'axios';
import DOMPurify from 'dompurify';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TEACHER_IMAGE = 'https://static.prod-images.emergentagent.com/jobs/7b2f5283-2e0b-4c29-9baf-4c93599e2df2/images/4d748bd4f0615b1c4e66f7281fb001ad79928bcb0c577911978afa4120b297d1.png';

export default function TeamPage() {
  const { language } = useLanguage();
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTeam();
  }, []);

  const fetchTeam = async () => {
    try {
      const response = await axios.get(`${API}/team`);
      setTeam(response.data);
    } catch (error) {
      console.error('Error fetching team:', error);
    } finally {
      setLoading(false);
    }
  };

  const sampleTeam = [
    {
      member_id: '1',
      name: 'Maria Garcia Lopez',
      role: 'Directora',
      role_en: 'Director',
      bio: 'Profesora de español con más de 20 años de experiencia en la enseñanza del español como lengua extranjera. Especializada en educación infantil y primaria.',
      bio_en: 'Spanish teacher with over 20 years of experience teaching Spanish as a foreign language. Specialized in early childhood and primary education.',
      photo: TEACHER_IMAGE,
      email: 'laura@colegioarce.com'
    }
  ];

  const displayTeam = team.length > 0 ? team : sampleTeam;

  const getBio = (member) => language === 'es' ? member.bio : (member.bio_en || member.bio);
  const getRole = (member) => language === 'es' ? member.role : (member.role_en || member.role);
  const getPhoto = (member) => {
    if (!member.photo) return TEACHER_IMAGE;
    if (member.photo.startsWith('/api')) return `${process.env.REACT_APP_BACKEND_URL}${member.photo}`;
    return member.photo;
  };

  const cleanHtml = (html) => {
    if (!html) return '';
    return DOMPurify.sanitize(html.replace(/&nbsp;/g, ' '));
  };

  // Build team schema for all members
  const teamSchema = {
    "@context": "https://schema.org",
    "@type": "EducationalOrganization",
    "name": "Colegio ARCE",
    "employee": displayTeam.map(m => buildPersonSchema(m))
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        titleES="Nuestro Equipo — Colegio ARCE | Profesores Nativos de Español"
        titleEN="Our Team — Colegio ARCE | Native Spanish Teachers"
        descES="Conoce al equipo de Colegio ARCE: profesores nativos de España con más de 15 años de experiencia. Formación, filosofía pedagógica y trayectoria de cada docente."
        descEN="Meet the Colegio ARCE team: native Spanish teachers with over 15 years of experience. Education background, teaching philosophy, and career path for each instructor."
        path="/team"
        schema={teamSchema}
        breadcrumbs={[
          { nameES: 'Inicio', nameEN: 'Home', path: '/' },
          { nameES: 'Equipo', nameEN: 'Team', path: '/team' }
        ]}
      />
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-40 pb-20 px-6 bg-[#F5F5F7]">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-sm tracking-wide uppercase text-[#E63946] font-semibold mb-4">
            {language === 'es' ? 'Nuestro Equipo' : 'Our Team'}
          </p>
          <h1 className="text-4xl sm:text-5xl font-semibold text-[#1D1D1F] mb-6" data-testid="team-title">
            {language === 'es' ? 'Profesionales apasionados' : 'Passionate Professionals'}
          </h1>
          <p className="text-lg text-[#86868B]">
            {language === 'es'
              ? 'Conoce a los profesores nativos que haran crecer el español de tus hijos.'
              : "Meet the native teachers who will help your children's Spanish grow."}
          </p>
        </div>
      </section>

      {/* Team Members - Full profiles, no modal */}
      <section className="section-padding bg-white">
        <div className="max-w-4xl mx-auto">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E63946]"></div>
            </div>
          ) : (
            <div className="space-y-16">
              {displayTeam.map((member) => (
                <article 
                  key={member.member_id} 
                  className="card-apple overflow-hidden"
                  data-testid={`team-member-${member.member_id}`}
                >
                  {/* Header with photo and name */}
                  <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 p-8 border-b border-black/5">
                    <img 
                      src={getPhoto(member)} 
                      alt={member.name}
                      loading="lazy"
                      width="128"
                      height="128"
                      className="w-32 h-32 rounded-2xl object-cover shrink-0"
                    />
                    <div className="text-center sm:text-left">
                      <h2 className="text-2xl font-semibold text-[#1D1D1F] mb-1">
                        {member.name}
                      </h2>
                      <p className="text-[#E63946] font-medium text-lg mb-3">
                        {getRole(member)}
                      </p>
                      {member.email && (
                        <a 
                          href={`mailto:${member.email}`}
                          className="inline-flex items-center gap-2 text-sm text-[#86868B] hover:text-[#E63946] transition-colors"
                          data-testid={`team-email-${member.member_id}`}
                        >
                          <Mail className="h-4 w-4" />
                          {member.email}
                        </a>
                      )}
                    </div>
                  </div>

                  {/* Full bio - directly in DOM for crawlers */}
                  <div className="p-8">
                    <div className="flex items-center gap-2 mb-4">
                      <BookOpen className="h-5 w-5 text-[#E63946]" />
                      <h3 className="text-lg font-semibold text-[#1D1D1F]">
                        {language === 'es' ? 'Biografia' : 'Biography'}
                      </h3>
                    </div>
                    <div 
                      className="text-[#1D1D1F] text-base leading-7 break-words"
                      style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                      dangerouslySetInnerHTML={{ __html: cleanHtml(getBio(member)) }}
                      data-testid={`team-bio-${member.member_id}`}
                    />

                    {/* Education section if present */}
                    {(member.education || member.education_en) && (
                      <div className="mt-8">
                        <div className="flex items-center gap-2 mb-4">
                          <GraduationCap className="h-5 w-5 text-[#E63946]" />
                          <h3 className="text-lg font-semibold text-[#1D1D1F]">
                            {language === 'es' ? 'Formacion' : 'Education'}
                          </h3>
                        </div>
                        <div 
                          className="text-[#86868B] text-base leading-7 break-words"
                          style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                          dangerouslySetInnerHTML={{ __html: cleanHtml(language === 'es' ? member.education : (member.education_en || member.education)) }}
                        />
                      </div>
                    )}

                    {/* Teaching Philosophy if present */}
                    {(member.philosophy || member.philosophy_en) && (
                      <div className="mt-8">
                        <div className="flex items-center gap-2 mb-4">
                          <BookOpen className="h-5 w-5 text-[#E63946]" />
                          <h3 className="text-lg font-semibold text-[#1D1D1F]">
                            {language === 'es' ? 'Filosofía pedagógica' : 'Teaching Philosophy'}
                          </h3>
                        </div>
                        <div 
                          className="text-[#86868B] text-base leading-7 break-words"
                          style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                          dangerouslySetInnerHTML={{ __html: cleanHtml(language === 'es' ? member.philosophy : (member.philosophy_en || member.philosophy)) }}
                        />
                      </div>
                    )}
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA to Contact */}
      <section className="section-padding bg-[#F5F5F7]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl font-semibold text-[#1D1D1F] mb-4">
            {language === 'es' ? '¿Quieres conocernos mejor?' : 'Want to know us better?'}
          </h2>
          <p className="text-[#86868B] mb-8">
            {language === 'es'
              ? 'Agenda una cita gratuita para hablar con nuestro equipo sobre el programa de tu hijo.'
              : "Schedule a free consultation to talk with our team about your child's program."}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="https://scheduler.zoom.us/laura-pallares-nieto/informacion-colegio-arce" target="_blank" rel="noopener noreferrer" className="btn-primary" data-testid="team-cta-contact">
              {language === 'es' ? 'Agenda una cita' : 'Schedule a consultation'}
            </a>
            <Link to="/courses" className="btn-secondary" data-testid="team-cta-courses">
              {language === 'es' ? 'Ver nuestros cursos' : 'View our courses'}
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
